/**
 * "Set status": every Monday UMED status label, always (Josie Evans, 2026-10-29, through Isaiah Cotto: "Josie wants these
 * buttons, give it to her." -- "I had multiple options and now I have 3 ... I need the SFS correction button back ... all
 * of them back").
 *
 * The eighteen labels of Monday's "Status (UMED)" picker, in the order of her screenshot and in Monday's own colours
 * (mondayPalette, UMED_STATUS), and since 2026-11-03 (SCRUM-129) eMerge's own APPROVED - SFS CORRECTION -- the partial
 * approval -- beside APPROVED: nineteen. None is ever hidden. Each is one of two things:
 *
 *   ENABLED   the existing act that produces that word, exactly as the row's status menu offers it -- the SAME tile
 *             (actTilesFor), so picking it opens the SAME panel with the same confirmation, required fields and notes,
 *             and the server writes the word in the act's own transaction (statusWords.js). Nothing here writes a status.
 *   DISABLED  with the reason, in plain words, that the act cannot apply to this case now. Whether a label is enabled is
 *             decided ONLY by actTilesFor (the row's own act availability), narrowed -- never widened -- by two server
 *             refusals the menu does not test: the fax guard (the server's fax_in_flight predicate on the row) and a
 *             closed case's CASE_CLOSED for a review or a correction. The reason is said from the same row facts and the
 *             same why-not helpers the status cell already shows; it never enables anything.
 *
 * A label with no act in eMerge is shown disabled ("Not available yet" -- see docs/umed/UMED_STATUS_PICKER.md, Gaps).
 * READY TO SUBMIT is the Patient Advocate's act on the Patient Advocate board: there is no UMED act for it.
 */
import React, { useEffect, useRef, useState } from 'react';
import { UMED_STATUS, mondayHex, filledChipStyle, normalizeMondayWord } from '../../theme/mondayPalette';
import type { BoardRow } from './umedApi';
import { PARTIAL_APPROVAL_WORD, PARTIAL_APPROVAL_HEX } from './umedApi';
import {
  BoardActs, ReadOnlyRow, CONTRADICTS_APPROVAL, partialApprovalOpen, plCorrectionHoldsCorrection, approvalStageOpen,
  approvalWhyNotNote, approvalEvidenceOnRecord, resubmittalWhyNotNote, RESUB_ALREADY_OPEN_NOTE, correctionNotSentNote,
  resubmittalStartStatus, RESUB_PIF_ONLY_NOTE,
  CORRECTION_RESUB_OPEN_NOTE, PIF_CORRECTION_COMPLETE_LABEL, CORRECTION_RESEND_LABEL,
} from './BoardTable';
import type { ActTile } from './BoardTable';
import './StatusPicker.css';

type Acts = React.ContextType<typeof BoardActs>;
type RowAct = ActTile['act'];

/** Monday's labels, in the order of the screenshot Josie sent (2026-10-29). */
export const PICKER_WORDS = [
  'READY TO SUBMIT', 'REVERSAL RESUB', 'REVERSAL', 'DCF RESUB NEEDED', 'PAID IN FULL CORRECTION', 'PROBLEM ACCOUNT',
  'DECEASED', 'UMED SUBMITTED TO DCF', 'NEEDS MCD RECERT', 'PAID IN FULL', 'SFS CORRECTION', 'NOCA-NO FLMMIS',
  'REVERSAL COMPLETE', 'SFS CORRECTION COMPLETED', 'SFS CORRECTION-FLMMIS ISSUE', 'REVERSAL NOT APPROVED', 'APPROVED',
  // 2026-11-03 (SCRUM-129): eMerge's partial approval, beside APPROVED -- not a Monday word, so not in her screenshot.
  'APPROVED - SFS CORRECTION',
  'NO LONGER QUALIFIES',
] as const;
export type PickerWord = typeof PICKER_WORDS[number];

export const SET_STATUS_LABEL = 'Set status';
/*
 * 2026-11-03 (SCRUM-129): "Under review for approval" and "New approvals – NOCA" are where Josie looks, so she looked for
 * them here. They are tabs, read from the case's words (readModel BOARD_TABS / LENSES), never a status anyone sets.
 */
export const TABS_NOT_STATUSES = 'Under review for approval ("Review for Approvals") and "New Approvals – NOCA" are tabs, not statuses. UMED SUBMITTED TO DCF or DCF RESUB NEEDED, with no correction, reversal or problem account open, puts a case under review; NOCA-NO FLMMIS, SFS CORRECTION-FLMMIS ISSUE or APPROVED - SFS CORRECTION puts it in New Approvals – NOCA.';
/** The reasons, in plain words. Exported so the tests read the same sentences. */
export const WHY = {
  READ_ONLY: 'A copy kept on the board: open the case to change anything.',
  NOT_PERMITTED: 'Your role cannot record this.',
  CLOSED: 'This case is closed.',
  ALREADY_APPROVED: 'Already approved. To undo an approval, start a reversal / DCF resubmittal or a correction.',
  APPROVED_HERE: 'Already approved.',
  /*
   * #555 (2026-10-29): the reversal start's own refusals, in the server's words (nativeWorkflowService
   * CLAIM_ALREADY_WITHDRAWN_WORDS and START_FAX_IN_FLIGHT_WORDS; resubmittalState.START_ALLOWED_WORDS). A backend test
   * (umed/__tests__/statusPickerWords.test.js) pins these copies to the server's.
   */
  START_ALLOWED: 'APPROVED, UMED SUBMITTED TO DCF (a first sending, or a DCF resubmission sent again), or SFS CORRECTION (SFS CORRECTION SUBMITTED included)',
  CLAIM_ALREADY_WITHDRAWN: 'This claim was already withdrawn by a reversal that DCF approved after its last sending; there is nothing left to reverse. An SFS correction still open on it is closed by a person. If the claim is sent to DCF again, a reversal can start after that sending.',
  START_FAX_IN_FLIGHT: 'A fax for this case is being sent to DCF (or its result is being recorded). Start the reversal after it is recorded.',
  NOT_SENT: 'Not sent to DCF yet.',
  ALREADY_SENT: 'Already sent to DCF.',
  FAX_IN_FLIGHT: 'A fax to DCF is in flight for this case. Wait for its result.',
  // 2026-11-03: the real reason (the server refuses a second exception, and a new correction beside an open one).
  CORRECTION_OPEN: 'A correction is open on this case: complete it first.',
  PIF_CORRECTION_OPEN: 'A paid in full correction is open on this case: send it again from the status menu, or complete it (PAID IN FULL CORRECTION · COMPLETED) before raising an SFS correction.',
  PARTIAL_NEEDS_FLMMIS: 'Record FLMMIS reflected for the approved months first.',
  PARTIAL_NOCA_IN_CASE: 'The NOCA on record cannot be carried by a review from here: record the review in the case first.',
  PARTIAL_APPROVAL_OPEN: 'A partial approval is open on this case: APPROVED completes it once FLMMIS shows the corrected months.',
  NO_CORRECTION: 'Needs an open correction.',
  NO_REVERSAL: 'No reversal / DCF resubmittal is open on this case. REVERSAL starts one.',
  REVERSAL_OPEN: 'A reversal / DCF resubmittal is open on this case.',
  REVERSAL_WITH_FINANCE: 'The DCF resubmittal is still with Finance: Finance hands it to UMED before it is sent.',
  REVERSAL_KIND_MISSING: 'The DCF resubmittal does not say yet whether it is full or corrected: state it in the case.',
  REVERSAL_SENT: 'Already sent to DCF: record DCF\'s answer (REVERSAL COMPLETE or REVERSAL NOT APPROVED).',
  REVERSAL_NOT_SENT: 'Not sent to DCF yet: record REVERSAL RESUB when it goes.',
  REVERSAL_DECLINED: 'DCF already declined it: send it again with REVERSAL RESUB.',
  SUSPENDED: 'The case is suspended (no longer qualifies, recert or deceased). Reactivate it under Disposition… first.',
  SAYS_THIS: 'The case already says this.',
  PAID_IN_FULL_ALREADY: 'Already recorded paid in full.',
  PIF_CORRECTION_TYPE: 'This is a paid in full correction: it completes as PAID IN FULL CORRECTION · COMPLETED.',
  OTHER_CORRECTION_TYPE: 'The correction open on this case is of another kind.',
  PARTIAL_OPEN: 'This partial approval\'s correction completes with APPROVED.',
  PL_OPEN: 'A PL correction completes with its PL correction outcome.',
  CORRECTION_NOT_SENT: 'The corrected UMED has not gone to DCF yet.',
  NOTHING_TO_RESEND: 'The correction is open and has not gone to DCF yet: there is nothing to send again.',
  DCF_RESUB_MOVED: 'Already moved to DCF RESUB NEEDED: the submitter was told.',
  OTHER_EXCEPTION: 'Another exception is open on this case.',
  PACKET_NOT_READY: 'The packet is not ready to send yet.',
  OUT_OF_SCOPE: 'This building is outside your approval scope.',
  REVIEW_IN_CASE: 'Record the review in the case first.',
  READY_TO_SUBMIT: 'Set by the Patient Advocate (Mark ready to submit, on the Patient Advocate board). UMED has no act for it.',
  NOT_AVAILABLE: 'Not available for this case right now.',
} as const;

export interface PickerButton {
  word: PickerWord;
  /** Monday's colour for the word. */
  hex: string | null;
  /** The act it runs (the status menu's own tile), or null when disabled. */
  act: RowAct | null;
  /** What the button says: the word, or the act's own label where it is not the word alone (SEND AGAIN, COMPLETED). */
  caption: string;
  /** Why it is disabled; null when enabled. */
  why: string | null;
}

const SUSPENDED: readonly string[] = ['NO_LONGER_QUALIFIES', 'NEEDS_RECERT', 'DECEASED'];
/** The status cell's own test of "approved" (BoardTable isApproved): the approval act is on record. */
const approved = (row: BoardRow) => row.approval?.canonical === 'PROVEN';

/** The acts the server refuses while eMerge's own fax of the case is in flight (409 FAX_IN_FLIGHT), unconditionally. */
const FAX_HELD: ReadonlySet<string> = new Set(['CORRECTION_COMPLETE', 'CORRECTION_RESEND', 'SUBMITTED_DCF', 'SUBMISSION', 'UPLOADED', 'FAX_DCF', 'RESUB_START']);
/**
 * The acts whose service locks the case without `allowClosed` (nativeWorkflowService.lockCase: 409 CASE_CLOSED) but which
 * the status menu still offers on a closed case (outcomesFor does not test the stage for a review or a correction). Said
 * as closed here rather than offered to be refused.
 */
const CLOSED_REFUSED: ReadonlySet<string> = new Set(['NOCA_NO_FLMMIS', 'SFS_CORRECTION', 'SFS_CORRECTION_FLMMIS_ISSUE', 'CORRECTION_COMPLETE', 'CORRECTION_RESEND']);

/**
 * Which existing tiles produce each word, first match wins. The correction re-send is the label of ITS correction's
 * kind (SFS CORRECTION for an SFS correction, the FLMMIS issue's own label for that one; a paid in full correction's
 * re-send stays in the status menu); a correction's completion is SFS CORRECTION COMPLETED, or -- for a paid in full
 * correction -- PAID IN FULL CORRECTION · COMPLETED, never the word alone (2026-10-29).
 */
/*
 * 2026-11-03 (SCRUM-129): with an SFS-family correction open (SFS correction or its FLMMIS-issue kind), BOTH SFS labels
 * are that correction sent again. Josie reached for "SFS CORRECTION" on an FLMMIS-issue correction and found it greyed
 * "one at a time": the act she wanted -- send the open one again -- was under the other label.
 */
const SFS_FAMILY: ReadonlySet<string> = new Set(['SFS_CORRECTION', 'SFS_CORRECTION_FLMMIS_ISSUE']);
function candidatesFor(word: PickerWord, row: BoardRow): RowAct[] {
  const type = row.correction?.correction_type || null;
  const pif = type === 'PAID_IN_FULL_CORRECTION';
  const sfsOpen = Boolean(type && SFS_FAMILY.has(type));
  switch (word) {
    case 'REVERSAL RESUB': return ['REVERSAL_SUBMITTED'];
    case 'REVERSAL': return ['RESUB_START'];
    case 'DCF RESUB NEEDED': return ['DCF_RESUB'];
    case 'PAID IN FULL CORRECTION': return pif ? ['PIF_CORRECTION', 'CORRECTION_COMPLETE'] : ['PIF_CORRECTION'];
    case 'PROBLEM ACCOUNT': return ['PROBLEM_ACCOUNT'];
    case 'DECEASED': return ['DECEASED'];
    case 'UMED SUBMITTED TO DCF': return ['SUBMITTED_DCF', 'SUBMISSION'];
    case 'NEEDS MCD RECERT': return ['NEEDS_MEDICAID_RECERT'];
    case 'PAID IN FULL': return ['PIF_DISPOSITION'];
    case 'SFS CORRECTION': return sfsOpen ? ['CORRECTION_RESEND'] : ['SFS_CORRECTION'];
    case 'NOCA-NO FLMMIS': return ['NOCA_NO_FLMMIS'];
    case 'REVERSAL COMPLETE': return ['REVERSAL_APPROVED'];
    case 'SFS CORRECTION COMPLETED': return pif ? [] : ['CORRECTION_COMPLETE'];
    case 'SFS CORRECTION-FLMMIS ISSUE': return sfsOpen ? ['CORRECTION_RESEND'] : ['SFS_CORRECTION_FLMMIS_ISSUE'];
    case 'REVERSAL NOT APPROVED': return ['REVERSAL_NOT_APPROVED'];
    case 'APPROVED': return ['PARTIAL_COMPLETE', 'APPROVAL'];
    case 'APPROVED - SFS CORRECTION': return ['PARTIAL_APPROVAL'];
    case 'NO LONGER QUALIFIES': return ['NO_LONGER_QUALIFIES'];
    default: return []; // READY TO SUBMIT: the Patient Advocate's act, not UMED's.
  }
}

/** The packet's first hold, in the server's words, or null. */
const packetHold = (row: BoardRow): string | null => {
  const prep = row.packet_preparation;
  return prep && prep.state === 'ACTION_REQUIRED' && prep.blockers.length ? prep.blockers[0].message : null;
};

/**
 * The start's refusal for a case in none of its four statuses (409 RESUBMITTAL_NOT_APPROVED), as the server words it
 * (nativeWorkflowService.notApprovedWords): the allowed statuses, the stage eMerge has, and the word.
 */
export function notStartableWords(row: Pick<BoardRow, 'stage' | 'source_status_raw' | 'exception'>): string {
  const at = row.stage ? String(row.stage).replace(/_/g, ' ').toLowerCase() : 'not approved';
  const word = row.source_status_raw ? normalizeMondayWord(row.source_status_raw) : null;
  const boardReversal = row.exception === 'REVERSAL' || /^\s*REVERSAL\s*$/i.test(String(row.source_status_raw || ''));
  return `A reversal / DCF resubmittal starts only on a case at ${WHY.START_ALLOWED}. eMerge has this case at ${at}, with no approval recorded`
    + `${boardReversal ? ' (a REVERSAL word on the Monday board is not an approval)' : (word ? ` (its status is ${word})` : '')}. If DCF approved it, record the approval first.`;
}

/** Why the label cannot apply now. Said from the row's facts in the order the acts check them; never decides availability. */
function whyNot(word: PickerWord, row: BoardRow, acts: NonNullable<Acts>, faxHeld: boolean): string {
  const closed = row.stage === 'CLOSED';
  const type = row.correction?.correction_type || null;
  const v = row.reversal || null;
  if (faxHeld) return word === 'REVERSAL' ? WHY.START_FAX_IN_FLIGHT : WHY.FAX_IN_FLIGHT;
  switch (word) {
    case 'READY TO SUBMIT':
      return WHY.READY_TO_SUBMIT;
    case 'REVERSAL RESUB':
      if (!acts.canReverse) return WHY.NOT_PERMITTED;
      if (closed) return WHY.CLOSED;
      if (!v) return WHY.NO_REVERSAL;
      if (v.state === 'SUBMITTED') return WHY.REVERSAL_SENT;
      if (v.handed === false) return WHY.REVERSAL_WITH_FINANCE;
      if (v.kind === null) return WHY.REVERSAL_KIND_MISSING;
      return WHY.NOT_AVAILABLE;
    case 'REVERSAL': {
      if (!acts.canReverse) return WHY.NOT_PERMITTED;
      if (closed) return WHY.CLOSED;
      if (v) return RESUB_ALREADY_OPEN_NOTE;
      // The start's own gate, in its order (startResubmittalInTx): no start status -> withdrawn, paid in full only, else
      // not one of the four statuses; then the board's holds (resubmittalWhyNotNote).
      if (!resubmittalStartStatus(row)) {
        if (row.reversal_claim_withdrawn) return WHY.CLAIM_ALREADY_WITHDRAWN;
        if (row.resubmittal_pif_only) return RESUB_PIF_ONLY_NOTE;
        return notStartableWords(row);
      }
      const note = resubmittalWhyNotNote(row, acts);
      if (note) return note.note;
      return WHY.NOT_AVAILABLE;
    }
    case 'REVERSAL COMPLETE':
    case 'REVERSAL NOT APPROVED':
      if (!acts.canReverse) return WHY.NOT_PERMITTED;
      if (closed) return WHY.CLOSED;
      if (!v) return WHY.NO_REVERSAL;
      if (v.state === 'REQUESTED') return WHY.REVERSAL_NOT_SENT;
      if (v.state === 'NOT_APPROVED') return WHY.REVERSAL_DECLINED;
      return WHY.NOT_AVAILABLE;
    case 'DCF RESUB NEEDED':
      if (!acts.canDispose) return WHY.NOT_PERMITTED;
      if (closed) return WHY.CLOSED;
      if (approved(row)) return WHY.ALREADY_APPROVED;
      if (v) return WHY.REVERSAL_OPEN;
      if (row.correction) return WHY.CORRECTION_OPEN;
      if (row.dcf_resub_moved) return WHY.DCF_RESUB_MOVED;
      if (row.resubmission && !row.resubmission.can_open) return row.resubmission.why || WHY.NOT_AVAILABLE;
      if (row.exception && !['NONE', 'CROSSOVER', 'RESUBMISSION'].includes(row.exception) && row.exception_state !== 'COMPLETE') return WHY.OTHER_EXCEPTION;
      return WHY.NOT_AVAILABLE;
    case 'PAID IN FULL CORRECTION':
      if (!acts.canCorrect || !acts.pifCorrection) return WHY.NOT_PERMITTED;
      if (closed) return WHY.CLOSED;
      if (row.correction && type !== 'PAID_IN_FULL_CORRECTION') return WHY.CORRECTION_OPEN;
      if (row.correction && row.correction.sent_since_opened === false) return correctionNotSentNote(row, acts)?.note || WHY.CORRECTION_NOT_SENT;
      if (!row.correction && v) return CORRECTION_RESUB_OPEN_NOTE;
      return WHY.NOT_AVAILABLE;
    case 'PROBLEM ACCOUNT':
      if (!acts.canDispose) return WHY.NOT_PERMITTED;
      if (closed) return WHY.CLOSED;
      if (row.disposition?.word === 'PROBLEM_ACCOUNT') return WHY.SAYS_THIS;
      if (approved(row)) return WHY.ALREADY_APPROVED;
      if (row.correction) return WHY.CORRECTION_OPEN;
      if (v) return WHY.REVERSAL_OPEN;
      return WHY.NOT_AVAILABLE;
    case 'DECEASED':
    case 'NEEDS MCD RECERT':
    case 'NO LONGER QUALIFIES': {
      const d = word === 'DECEASED' ? 'DECEASED' : word === 'NEEDS MCD RECERT' ? 'NEEDS_MEDICAID_RECERT' : 'NO_LONGER_QUALIFIES';
      if (!acts.canDispose) return WHY.NOT_PERMITTED;
      if (closed) return WHY.CLOSED;
      if (row.disposition?.word === d) return WHY.SAYS_THIS;
      if (approved(row) && CONTRADICTS_APPROVAL.includes(d)) return WHY.ALREADY_APPROVED;
      return WHY.NOT_AVAILABLE;
    }
    case 'UMED SUBMITTED TO DCF': {
      if (!acts.canMarkSubmitted) return WHY.NOT_PERMITTED;
      if (closed) return WHY.CLOSED;
      if (approved(row)) return WHY.APPROVED_HERE;
      if (SUSPENDED.includes(row.lifecycle || '')) return WHY.SUSPENDED;
      if (row.submission_recorded || ((row.sent_to_dcf_on || row.submitted_on) && row.stage === 'SUBMITTED')) return WHY.ALREADY_SENT;
      const hold = packetHold(row);
      if (hold) return `${WHY.PACKET_NOT_READY} ${hold}`;
      if (['CREATED', 'NEEDS_DCF_ACCOUNT', 'READY'].includes(row.stage) && row.packet_preparation?.state !== 'READY') return WHY.PACKET_NOT_READY;
      return WHY.NOT_AVAILABLE;
    }
    case 'PAID IN FULL':
      if (!acts.canDispose) return WHY.NOT_PERMITTED;
      if (closed) return WHY.CLOSED;
      if (row.finance_state === 'PAID_IN_FULL' || row.disposition?.word === 'PAID_IN_FULL') return WHY.PAID_IN_FULL_ALREADY;
      if (v) return WHY.REVERSAL_OPEN;
      return WHY.NOT_AVAILABLE;
    case 'SFS CORRECTION':
    case 'SFS CORRECTION-FLMMIS ISSUE': {
      // An open SFS-family correction is sent again under either label (SFS_FAMILY): that needs only manage_corrections;
      // raising a new one needs its type too.
      const resendsOpen = Boolean(row.correction && type && SFS_FAMILY.has(type));
      const typeOffered = word === 'SFS CORRECTION' ? acts.sfs : acts.flmmisIssue;
      if (!acts.canCorrect || (!resendsOpen && !typeOffered)) return WHY.NOT_PERMITTED;
      if (row.correction) {
        if (!type || !SFS_FAMILY.has(type)) return type === 'PAID_IN_FULL_CORRECTION' ? WHY.PIF_CORRECTION_OPEN : WHY.CORRECTION_OPEN;
        if (closed) return WHY.CLOSED;
        if (v) return WHY.REVERSAL_OPEN;
        if (row.correction.sent_since_opened !== true) return correctionNotSentNote(row, acts)?.note || WHY.NOTHING_TO_RESEND;
        return WHY.NOT_AVAILABLE;
      }
      if (v) return CORRECTION_RESUB_OPEN_NOTE;
      return WHY.NOT_AVAILABLE;
    }
    case 'SFS CORRECTION COMPLETED':
      if (!acts.canCorrect) return WHY.NOT_PERMITTED;
      if (!row.correction) return WHY.NO_CORRECTION;
      if (type === 'PAID_IN_FULL_CORRECTION') return WHY.PIF_CORRECTION_TYPE;
      if (partialApprovalOpen(row)) return WHY.PARTIAL_OPEN;
      if (plCorrectionHoldsCorrection(row)) return WHY.PL_OPEN;
      // #543 (CORRECTION_NOT_SENT): a correction completes only once its corrected UMED went to DCF.
      if (row.correction.sent_since_opened === false) return correctionNotSentNote(row, acts)?.note || WHY.CORRECTION_NOT_SENT;
      return WHY.NOT_AVAILABLE;
    case 'NOCA-NO FLMMIS':
      if (!acts.canReview) return WHY.NOT_PERMITTED;
      if (approved(row)) return WHY.ALREADY_APPROVED;
      return WHY.NOT_AVAILABLE;
    case 'APPROVED': {
      if (!acts.canReview) return WHY.NOT_PERMITTED;
      if (approved(row)) return WHY.APPROVED_HERE;
      if (closed) return WHY.CLOSED;
      if (SUSPENDED.includes(row.lifecycle || '')) return WHY.SUSPENDED;
      const note = approvalWhyNotNote(row, true);
      if (note) return note;
      if (!approvalStageOpen(row)) return WHY.NOT_SENT;
      if (row.approval.in_approval_scope !== true) return WHY.OUT_OF_SCOPE;
      if (approvalEvidenceOnRecord(row)) return WHY.OUT_OF_SCOPE;
      return WHY.REVIEW_IN_CASE;
    }
    // 2026-11-03 (SCRUM-129): the partial approval (partialApprovalOffered: FLMMIS reflected for the approved months).
    case 'APPROVED - SFS CORRECTION':
      if (!acts.canReview) return WHY.NOT_PERMITTED;
      if (partialApprovalOpen(row)) return WHY.PARTIAL_APPROVAL_OPEN;
      if (approved(row)) return WHY.APPROVED_HERE;
      if (closed) return WHY.CLOSED;
      if (SUSPENDED.includes(row.lifecycle || '')) return WHY.SUSPENDED;
      if (!approvalStageOpen(row)) return WHY.NOT_SENT;
      /*
       * Verification of #580 (R3): partialApprovalOffered's own order (BoardTable) -- FLMMIS reflected first; then, unless
       * the approval is ready, the building in this reviewer's approval scope and a NOCA the review can record.
       */
      if (row.approval.flmmis !== 'REFLECTED') return WHY.PARTIAL_NEEDS_FLMMIS;
      if (row.approval.ready_to_approve !== true && row.approval.in_approval_scope !== true) return WHY.OUT_OF_SCOPE;
      if (row.approval.ready_to_approve !== true) return WHY.PARTIAL_NOCA_IN_CASE;
      return WHY.NOT_AVAILABLE;
    default:
      return WHY.NOT_AVAILABLE;
  }
}

/** The caption of an enabled button: the word, or the act's own label where the act is not the word alone. */
/** The open correction's kind, said on a SEND AGAIN under the other SFS label (2026-11-03): what is actually sent again. */
const RESEND_KIND: Record<string, string> = { SFS_CORRECTION: 'the open SFS correction', SFS_CORRECTION_FLMMIS_ISSUE: 'the open FLMMIS-issue correction' };
const WORD_TYPE: Partial<Record<PickerWord, string>> = { 'SFS CORRECTION': 'SFS_CORRECTION', 'SFS CORRECTION-FLMMIS ISSUE': 'SFS_CORRECTION_FLMMIS_ISSUE' };
function captionOf(word: PickerWord, act: RowAct | null, row?: BoardRow): string {
  if (act === 'CORRECTION_RESEND') {
    const base = word === 'SFS CORRECTION' ? CORRECTION_RESEND_LABEL : `${word} · SEND AGAIN`;
    const open = row?.correction?.correction_type || null;
    return open && WORD_TYPE[word] && WORD_TYPE[word] !== open && RESEND_KIND[open] ? `${base} (${RESEND_KIND[open]})` : base;
  }
  if (act === 'CORRECTION_COMPLETE' && word === 'PAID IN FULL CORRECTION') return PIF_CORRECTION_COMPLETE_LABEL;
  return word;
}

/**
 * The eighteen buttons for this row and this viewer. `tiles` is the row's status menu (actTilesFor): a label is enabled
 * only by a tile there. Pure.
 */
export function statusPickerButtons(row: BoardRow, acts: Acts, tiles: readonly ActTile[], { readOnly = false } = {}): PickerButton[] {
  /*
   * A tile the status menu draws DISABLED (the grey-tiles change, fix/umed-grey-unsent-correction-tiles: `disabled` with
   * its reason) is not offered: its label stays disabled here, with the menu's own reason.
   */
  const heldReason = (t: ActTile): string | null => {
    const d = (t as { disabled?: { reason?: string } | null }).disabled;
    return d ? (d.reason || WHY.NOT_AVAILABLE) : null;
  };
  const offered = new Set(tiles.filter((t) => !heldReason(t)).map((t) => t.act));
  const greyed = new Map(tiles.filter((t) => heldReason(t)).map((t) => [t.act, heldReason(t) as string]));
  return PICKER_WORDS.map((word) => {
    // APPROVED - SFS CORRECTION is eMerge's word: its colour is the legend's own (PARTIAL_APPROVAL_HEX), as on the status menu.
    const hex = mondayHex(UMED_STATUS, word) || (word === PARTIAL_APPROVAL_WORD ? PARTIAL_APPROVAL_HEX : null);
    if (readOnly) return { word, hex, act: null, caption: word, why: WHY.READ_ONLY };
    if (!acts) return { word, hex, act: null, caption: word, why: WHY.NOT_PERMITTED };
    const menuSays = candidatesFor(word, row).map((a) => greyed.get(a)).find(Boolean);
    const act = candidatesFor(word, row).find((a) => offered.has(a)) || null;
    if (!act && menuSays) return { word, hex, act: null, caption: word, why: menuSays };
    if (act && row.stage === 'CLOSED' && CLOSED_REFUSED.has(act)) return { word, hex, act: null, caption: word, why: WHY.CLOSED };
    const faxHeld = Boolean(act && row.fax_in_flight && FAX_HELD.has(act));
    if (act && !faxHeld) return { word, hex, act, caption: captionOf(word, act, row), why: null };
    return { word, hex, act: null, caption: word, why: whyNot(word, row, acts, faxHeld) };
  });
}

/** Whether this viewer may record any UMED act at all: the picker is offered only then. */
export const pickerOffered = (acts: Acts): boolean =>
  Boolean(acts && (acts.canReview || acts.canCorrect || acts.canDispose || acts.canReverse || acts.canMarkSubmitted));

/**
 * The "Set status" control: a button beside the status chip, and the eighteen labels it opens. `onPick` is the status
 * cell's own handler for a tile of its menu, so an enabled label goes exactly where that tile goes. Escape closes and
 * gives focus back to the button; so does a press anywhere else.
 */
export default function StatusPicker({ row, tiles, onPick, onOpen, name, toggleRef }: {
  row: BoardRow; tiles: readonly ActTile[]; onPick: (act: RowAct) => void; onOpen?: () => void; name: string;
  /** The status cell's handle on the Set status button: a panel opened from here gives focus back to it. */
  toggleRef?: React.MutableRefObject<HTMLButtonElement | null>;
}) {
  const acts = React.useContext(BoardActs);
  const readOnly = React.useContext(ReadOnlyRow);
  const [open, setOpen] = useState(false);
  const ownToggle = useRef<HTMLButtonElement | null>(null);
  const toggle = toggleRef || ownToggle;
  const wrap = useRef<HTMLSpanElement | null>(null);
  const first = useRef<HTMLButtonElement | null>(null);
  const id = `um-statuspick-${row.id}`;
  useEffect(() => { if (open) first.current?.focus({ preventScroll: true }); }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);
  if (!pickerOffered(acts) && !readOnly) return null;
  const close = () => { setOpen(false); toggle.current?.focus(); };
  const buttons = open ? statusPickerButtons(row, acts, tiles, { readOnly }) : [];
  return (
    <span className="um-statuspick" ref={wrap} onClick={(e) => e.stopPropagation()}>
      <button type="button" className="um-statuspick-toggle" ref={toggle} aria-expanded={open} aria-controls={open ? id : undefined}
        aria-label={`${SET_STATUS_LABEL} for ${name}`} onClick={() => { if (open) setOpen(false); else { onOpen?.(); setOpen(true); } }}>
        {SET_STATUS_LABEL}
      </button>
      {open && (
        <div className="um-statuspick-panel" id={id} role="group" aria-label={`${SET_STATUS_LABEL} for ${name}`}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); } }}>
          <p className="um-statuspick-head">Every Monday status. Each one records the eMerge act that produces it, and asks before anything is saved; a grey one says why it cannot apply now.</p>
          <p className="um-statuspick-head" data-testid="statuspick-tabs-note">{TABS_NOT_STATUSES}</p>
          <div className="um-statuspick-grid">
            {buttons.map((b, i) => {
              const whyId = `${id}-why-${i}`;
              const style = b.hex
                ? (b.act ? filledChipStyle(b.hex) : ({ ['--swatch' as any]: b.hex } as React.CSSProperties))
                : undefined;
              return (
                <span className="um-statuspick-item" key={b.word}>
                  <button type="button" className="um-statuspick-btn" data-word={b.word} data-act={b.act || undefined}
                    ref={i === 0 ? first : undefined} style={style}
                    aria-disabled={b.act ? undefined : 'true'} aria-describedby={b.why ? whyId : undefined}
                    onClick={() => {
                      if (!b.act) return;
                      setOpen(false);
                      onPick(b.act);
                    }}>
                    {b.caption}
                  </button>
                  {b.why && <span className="um-statuspick-why" id={whyId}>{b.why}</span>}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </span>
  );
}
